<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, shallowRef, watch } from 'vue';
import { RouterLink, useRoute } from 'vue-router';
import WalletConnect from '@src/features/wallet/WalletConnect.vue';
import backpackFilledIcon from '@src/shared/assets/icons/backpack-filled.svg';
import backpackIcon from '@src/shared/assets/icons/backpack.svg';
import layersFilledIcon from '@src/shared/assets/icons/layers-filled.svg';
import layersIcon from '@src/shared/assets/icons/layers.svg';
import userFilledIcon from '@src/shared/assets/icons/user-filled.svg';
import userIcon from '@src/shared/assets/icons/user.svg';
import { ROUTE_NAMES } from '@src/shared/constants/routes';
import { useSelectAppChain } from '@src/shared/lib/composables/useSelectAppChain';
import { useWalletSession } from '@src/shared/lib/composables/useWalletSession';
import { useAppConfigStore } from '@src/shared/stores/app-config';
import AppLogo from '@src/shared/ui/app-logo/AppLogo.vue';
import FooterLink from '@src/shared/ui/FooterLink.vue';
import MobileSelect from '@src/shared/ui/MobileSelect.vue';

const route = useRoute();
const store = useAppConfigStore();
const { selectChain } = useSelectAppChain();
const { config, chains, selectedChain, selectedChainCode, selectedCurrencyCode, selectedLanguageCode } = storeToRefs(store);
const { disconnect, isConnected } = useWalletSession();

const links = [
	{ name: ROUTE_NAMES.earn, label: 'Earn', icon: layersIcon, iconActive: layersFilledIcon },
	{ name: ROUTE_NAMES.portfolio, label: 'Portfolio', icon: backpackIcon, iconActive: backpackFilledIcon },
	{ name: ROUTE_NAMES.profile, label: 'Profile', icon: userIcon, iconActive: userFilledIcon },
] as const;

const activeIndex = computed(() => {
	const i = links.findIndex((link) => link.name === route.name);
	return i === -1 ? 0 : i;
});

const navEl = ref<HTMLElement | null>(null);
const thumb = shallowRef({ left: 0, width: 0 });

function syncThumb() {
	const nav = navEl.value;
	if (!nav) {
		return;
	}
	const anchors = nav.querySelectorAll<HTMLElement>('a.nav-link');
	const el = anchors[activeIndex.value];
	if (!el) {
		return;
	}
	const n = nav.getBoundingClientRect();
	const r = el.getBoundingClientRect();
	thumb.value = {
		left: r.left - n.left,
		width: r.width,
	};
}

function scheduleSync() {
	void nextTick(syncThumb);
}

let resizeObserver: null | ResizeObserver = null;

onMounted(() => {
	scheduleSync();
	resizeObserver = new ResizeObserver(scheduleSync);
	if (navEl.value) {
		resizeObserver.observe(navEl.value);
	}
	resizeObserver.observe(globalThis.document.documentElement);
});

onBeforeUnmount(() => {
	resizeObserver?.disconnect();
	resizeObserver = null;
});

watch(activeIndex, scheduleSync);

function iconMaskStyle(icon: string) {
	// Кавычки обязательны: Vite инлайнит мелкие svg в data-URI с апострофами,
	// без кавычек такой url() невалиден и маска не применяется.
	return {
		WebkitMaskImage: `url("${icon}")`,
		maskImage: `url("${icon}")`,
	};
}

const isMenuOpen = ref(false);
const swipeOffsetX = ref(0);
const isDragging = ref(false);

const SWIPE_CLOSE_PX = 80;
const MENU_ANIMATION_MS = 350;

let touchStartX = 0;

function menuOffscreenX() {
	return -globalThis.innerWidth;
}

function onMenuTouchStart(event: TouchEvent) {
	touchStartX = event.touches[0]?.clientX ?? 0;
}

function onMenuTouchMove(event: TouchEvent) {
	const touch = event.touches[0];
	if (!touch) {
		return;
	}

	const deltaX = Math.min(0, touch.clientX - touchStartX);
	if (deltaX === 0) {
		return;
	}

	isDragging.value = true;
	swipeOffsetX.value = deltaX;
}

function onMenuTouchEnd() {
	isDragging.value = false;

	if (swipeOffsetX.value <= -SWIPE_CLOSE_PX) {
		closeMenu();
		return;
	}

	swipeOffsetX.value = 0;
}

const menuPanelStyle = computed(() => ({
	transform: `translateX(${swipeOffsetX.value}px)`,
}));

const languageOptions = computed(() =>
	(config.value?.languages ?? []).map((language) => ({ value: language.code, label: language.name })),
);

const currencyOptions = computed(() =>
	(config.value?.currencies ?? []).map((currency) => ({
		value: currency.code,
		label: `${currency.symbol} ${currency.code.toUpperCase()}`,
	})),
);

const networkOptions = computed(() =>
	chains.value.map((chain) => ({ value: chain.code, label: chain.name, iconUrl: chain.iconUrl })),
);

const selectedLanguageName = computed(() => {
	const code = selectedLanguageCode.value;
	return config.value?.languages.find((language) => language.code === code)?.name ?? code.toUpperCase();
});

function openMenu() {
	isMenuOpen.value = true;
	swipeOffsetX.value = menuOffscreenX();
	requestAnimationFrame(() => {
		swipeOffsetX.value = 0;
	});
}

function closeMenu() {
	if (!isMenuOpen.value) {
		return;
	}

	swipeOffsetX.value = menuOffscreenX();
	globalThis.setTimeout(() => {
		isMenuOpen.value = false;
		swipeOffsetX.value = 0;
	}, MENU_ANIMATION_MS);
}

function onMenuKeydown(event: KeyboardEvent) {
	if (event.key === 'Escape' && isMenuOpen.value) {
		closeMenu();
	}
}

function onDisconnect() {
	disconnect();
	closeMenu();
}

function onNetworkSelect(code: string) {
	void selectChain(code);
	closeMenu();
}

watch(
	() => isMenuOpen.value,
	(open) => {
		document.body.style.overflow = open ? 'hidden' : '';
	},
);

watch(
	() => route.fullPath,
	() => {
		closeMenu();
	},
);

onMounted(() => {
	document.addEventListener('keydown', onMenuKeydown);
});

onBeforeUnmount(() => {
	document.removeEventListener('keydown', onMenuKeydown);
	document.body.style.overflow = '';
});
</script>

<template>
	<div class="fixed inset-x-0 bottom-4 z-30 px-4 sm:hidden">
		<div class="flex items-center gap-3">
			<nav
				ref="navEl"
				class="relative flex h-15 flex-1 rounded-full border border-cardBorder bg-cardBgFade p-1 backdrop-blur-xl"
				aria-label="Main"
			>
				<div
					class="pointer-events-none absolute inset-y-1 rounded-full bg-white/10 transition-[left,width] duration-200 ease-in-out"
					aria-hidden="true"
					:style="{ left: `${thumb.left}px`, width: `${thumb.width}px` }"
				/>
				<RouterLink
					v-for="(link, idx) in links"
					:key="link.name"
					:to="{ name: link.name }"
					class="nav-link relative z-10 flex flex-1 flex-col items-center justify-center gap-1 rounded-full transition-colors duration-200"
					:class="activeIndex === idx ? 'text-brandColor' : 'text-primaryText'"
				>
					<span
						class="size-5.5 bg-current mask-size-contain mask-center mask-no-repeat"
						:style="iconMaskStyle(activeIndex === idx ? link.iconActive : link.icon)"
						aria-hidden="true"
					/>
					<span class="text-xs leading-none font-light">{{ link.label }}</span>
				</RouterLink>
			</nav>

			<button
				type="button"
				class="flex size-15 shrink-0 cursor-pointer items-center justify-center rounded-full border border-cardBorder bg-cardBgFade transition-all duration-200 hover:border-cardBorderHover active:scale-[0.90] backdrop-blur-xl"
				aria-label="Open menu"
				@click="openMenu"
			>
				<img src="/src/shared/assets/icons/menu-mob.svg" class="size-8" alt="" />
			</button>
		</div>
	</div>

	<Teleport to="body">
		<div
			v-if="isMenuOpen"
			class="menu-panel fixed inset-0 z-50 flex flex-col bg-black px-5 pb-8 pt-11 text-primaryText sm:hidden"
			:class="{ 'menu-panel--dragging': isDragging }"
			:style="menuPanelStyle"
			role="dialog"
			aria-modal="true"
			aria-label="Menu"
			@touchstart.passive="onMenuTouchStart"
			@touchmove.passive="onMenuTouchMove"
			@touchend.passive="onMenuTouchEnd"
		>
			<header class="flex shrink-0 items-center gap-3 py-4">
				<AppLogo />

				<WalletConnect class="ml-auto" />

				<button
					type="button"
					class="bg-cardBgFade border border-cardBorder size-10 rounded-full flex items-center justify-center transition-all duration-200 active:scale-[0.90]"
					aria-label="Close menu"
					@click="closeMenu"
				>
					<img src="/src/shared/assets/icons/close.svg" class="size-4" alt="" />
				</button>
			</header>

			<div class="mt-12 flex min-h-0 flex-1 flex-col overflow-y-auto">
				<p class="text-sm text-brandColor">Navigation /</p>
				<nav class="mt-4 flex flex-col gap-5" aria-label="Main">
					<RouterLink
						v-for="link in links"
						:key="link.name"
						:to="{ name: link.name }"
						class="text-4xl leading-none font-normal transition-colors"
						:class="route.name === link.name ? 'text-primaryText' : 'text-hintText'"
					>
						{{ link.label }}
					</RouterLink>
				</nav>

				<div v-if="config" class="mt-auto grid grid-cols-2 gap-8">
					<div>
						<p class="text-sm text-brandColor">Terms /</p>
						<ul class="mt-3 flex flex-col gap-3">
							<li>
								<footer-link name="Cookie" :link="config.links.cookie" />
							</li>
							<li>
								<footer-link name="Privacy" :link="config.links.privacy" />
							</li>
							<li>
								<footer-link name="Terms of use" :link="config.links.tos" />
							</li>
						</ul>
					</div>

					<div>
						<p class="text-sm text-brandColor">Socials /</p>
						<ul class="mt-3 flex flex-col gap-3">
							<li>
								<footer-link name="Discord" :link="config.socialUrls.discord" />
							</li>
							<li>
								<footer-link name="Telegram" :link="config.socialUrls.telegram" />
							</li>
							<li>
								<footer-link name="X / Twitter" :link="config.socialUrls.twitter" />
							</li>
						</ul>
					</div>
				</div>

				<div class="flex flex-col gap-2 mt-12">
					<div v-if="config" class="flex gap-2">
						<MobileSelect
							class="flex-1"
							:options="languageOptions"
							:model-value="selectedLanguageCode"
							:label="selectedLanguageName"
							@select="store.selectLanguage"
						/>
						<MobileSelect
							class="flex-1"
							:options="currencyOptions"
							:model-value="selectedCurrencyCode"
							:label="selectedCurrencyCode.toUpperCase()"
							@select="store.selectCurrency"
						/>
					</div>

					<MobileSelect
						v-if="selectedChain"
						:options="networkOptions"
						:model-value="selectedChainCode"
						:label="selectedChain.name"
						:icon-url="selectedChain.iconUrl"
						@select="onNetworkSelect"
					/>

					<button
						v-if="isConnected"
						type="button"
						class="h-12 w-full cursor-pointer font-light rounded-lg bg-errorColor/15 text-sm text-errorColor border border-errorColor/30"
						@click="onDisconnect"
					>
						Disconnect Wallet
					</button>
				</div>
			</div>
		</div>
	</Teleport>
</template>

<style scoped>
.menu-panel {
	transition: transform 0.35s cubic-bezier(0.32, 0.72, 0, 1);
	will-change: transform;
}

.menu-panel--dragging {
	transition: none;
}
</style>
